import { useEffect, useMemo } from 'react'
import { useMatches } from '@tanstack/react-router'
import { atom, useSetAtom } from 'jotai'

/**
 * Header config a route can declare via TanStack Router's `staticData`.
 * Static fields land in the unified shell's header bar; dynamic action
 * buttons render through `<PageHeaderActions>` from inside the page.
 *
 *   export const Route = createFileRoute('/...')({
 *     staticData: { pageHeader: { title: 'Vendors' } },
 *     component: VendorsPage,
 *   })
 */
export type ScreenataPageWidth = 'full' | 'reading' | 'document'
export type EmployeeCenterLayout = 'padded' | 'fullBleed'

export interface PageHeaderConfig {
  title: string
  /**
   * Makes the title a link back to its section index — for drill-down pages
   * that keep the section title and append the record via `<PageHeaderExtra>`
   * (e.g. `Frameworks › SOC 2`), where the title reads like a breadcrumb node
   * but was dead text.
   *
   * Workspace-relative, like `SettingSidebar`'s hrefs (`/frameworks`), and
   * resolved against the current `/workspaces/$workspaceId`. Omit it on the
   * index page itself — linking a page to itself is noise.
   */
  titleHref?: string
  /** Width cap for the main content column. Defaults to 'full'. */
  width?: ScreenataPageWidth
  /** When true, the page renders its own MainHeader and the shell skips it. */
  customHeader?: boolean
  /** When false, the shell skips its `space-y-6 p-6` content wrapper.
   *  Defaults to true to match the original ScreenataPageLayout behavior. */
  pad?: boolean
  /** When false, the middle layout does not own page scrolling. Defaults to true for route-driven pages. */
  scroll?: boolean
  /** Hide the route icon and visible title while keeping `title` for the document title. */
  hideVisibleTitle?: boolean
}

type RouteStaticData = {
  employeeCenterLayout?: EmployeeCenterLayout
}

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    pageHeader?: PageHeaderConfig
    /** Employee Center shell content layout. Defaults to `padded`. */
    employeeCenterLayout?: EmployeeCenterLayout
  }
}

/** Returns the most specific matched route's `pageHeader`, or null. */
export function usePageHeaderConfig(): PageHeaderConfig | null {
  const matches = useMatches()
  return useMemo(() => {
    for (let i = matches.length - 1; i >= 0; i--) {
      const cfg = matches[i]?.staticData?.pageHeader
      if (cfg) return cfg
    }
    return null
  }, [matches])
}

/** Returns the most specific matched Employee Center layout, defaulting to padded. */
export function getEmployeeCenterLayout(
  matches: ReadonlyArray<{ staticData?: RouteStaticData }>,
): EmployeeCenterLayout {
  for (let i = matches.length - 1; i >= 0; i--) {
    const layout = matches[i]?.staticData?.employeeCenterLayout
    if (layout) return layout
  }
  return 'padded'
}

export const PAGE_HEADER_BREADCRUMB_SLOT_ID = 'screenata-page-header-breadcrumb'
export const PAGE_HEADER_EXTRA_SLOT_ID = 'screenata-page-header-extra'

/**
 * The Controls surface's detail-pane header strip. The pane is owned by
 * ControlsTestsPage but its contents (the open Test's code and actions) are
 * known only to the detail page rendered inside it, so the page portals them up
 * rather than the pane re-deriving them.
 */
export const DETAIL_PANE_HEADER_SLOT_ID = 'detail-pane-header-slot'
export const DETAIL_PANE_ACTIONS_SLOT_ID = 'detail-pane-actions-slot'
export const PAGE_HEADER_ACTIONS_SLOT_ID = 'screenata-page-header-actions'

/**
 * Dynamic title override. Detail pages (where the title comes from
 * loaded data, e.g. a vendor name) call `usePageHeaderTitle(name)` to
 * publish the live title; the shell reads this atom and prefers it
 * over the static `pageHeader.title` from route metadata.
 */
export const pageHeaderDynamicTitleAtom = atom<string | null>(null)

export function usePageHeaderTitle(title: string | null | undefined) {
  const set = useSetAtom(pageHeaderDynamicTitleAtom)
  useEffect(() => {
    if (!title) return
    set(title)
    return () => set(null)
  }, [set, title])
}
